// P05-5-4: Suggestion Card Component
// Displays coach or challenge suggestions with follow/join actions

import { User, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CoachSuggestion {
  type: 'coach';
  user_id: string;
  full_name: string;
  coach_bio: string | null;
  follower_count?: number;
}

interface ChallengeSuggestion {
  type: 'challenge';
  id: string;
  title: string;
  description: string | null;
  coach_id: string;
  coach_name: string;
  end_at: string;
}

type Suggestion = CoachSuggestion | ChallengeSuggestion;

interface SuggestionCardProps {
  suggestion: Suggestion;
  onFollow?: (coachId: string) => void;
  onJoinChallenge?: (challengeId: string) => void;
}

export function SuggestionCard({ suggestion, onFollow, onJoinChallenge }: SuggestionCardProps) {
  const queryClient = useQueryClient();

  const followCoach = useMutation({
    mutationFn: async (coachId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/feed-follow-toggle`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: coachId }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to follow coach' }));
        throw new Error(error.error || 'Failed to follow coach');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed', 'following'] });
      onFollow?.(suggestion.type === 'coach' ? suggestion.user_id : '');
    },
  });

  const joinChallenge = useMutation({
    mutationFn: async (challengeId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/coach-challenge-join`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ challenge_id: challengeId }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to join challenge' }));
        throw new Error(error.error || 'Failed to join challenge');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      onJoinChallenge?.(suggestion.type === 'challenge' ? suggestion.id : '');
    },
  });

  if (suggestion.type === 'coach') {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <Avatar>
              <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${suggestion.user_id}`} />
              <AvatarFallback>
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base">{suggestion.full_name}</CardTitle>
              {suggestion.follower_count !== undefined && (
                <CardDescription className="text-xs">
                  {suggestion.follower_count} {suggestion.follower_count === 1 ? 'follower' : 'followers'}
                </CardDescription>
              )}
            </div>
          </div>
        </CardHeader>
        {suggestion.coach_bio && (
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {suggestion.coach_bio}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => followCoach.mutate(suggestion.user_id)}
              disabled={followCoach.isPending}
              className="w-full"
            >
              {followCoach.isPending ? 'Following...' : 'Follow'}
            </Button>
          </CardContent>
        )}
      </Card>
    );
  }

  // Challenge suggestion
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[#3DC68D]/10 p-2">
            <Trophy className="h-4 w-4 text-[#3DC68D]" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{suggestion.title}</CardTitle>
            <CardDescription className="text-xs">by {suggestion.coach_name}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {suggestion.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {suggestion.description}
          </p>
        )}
        <Button
          size="sm"
          onClick={() => joinChallenge.mutate(suggestion.id)}
          disabled={joinChallenge.isPending}
          className="w-full bg-[#3DC68D] hover:bg-[#3DC68D]/90"
        >
          {joinChallenge.isPending ? 'Joining...' : 'Join Challenge'}
        </Button>
      </CardContent>
    </Card>
  );
}

